import { omit } from 'lodash'

// how the runner keys a node's collected values (its sanitize_tool_name)
const runnerKey = (name: string) =>
  name
    .trim()
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .toLowerCase()

interface OwnVariableSource {
  channel?: string | null
  extract_variables?: ({ variable_name?: string | null } | null | undefined)[] | null
}

/** The node's own Variables, which the runner fills only on a V2.9 or v2.10 node on a call. */
export const ownVariableNames = (values: OwnVariableSource, ownVariables: boolean): string[] =>
  ownVariables && values.channel === 'call'
    ? (values.extract_variables ?? []).map((row) => row?.variable_name?.trim() ?? '')
    : []

interface HttpRequestTemplates {
  http_url?: string | null
  http_headers?: string | null
  http_params?: string | null
  http_body_type?: string | null
  http_body_template?: string | null
}

/** The runner fills an own Variable's {{slot}} only when the tool schema does not list it. */
export const dropOwnVariableSlots = (
  schema: Record<string, unknown>,
  ownNames: string[],
  action: HttpRequestTemplates,
): Record<string, unknown> => {
  const sendsBody = (action.http_body_type || 'none') !== 'none'
  // with no body template the runner sends the model's arguments as the body, so none may go
  if (sendsBody && !action.http_body_template?.trim()) return schema
  const own = new Set(ownNames.map(runnerKey))
  const templates = [action.http_url, action.http_headers, action.http_params]
  if (sendsBody) templates.push(action.http_body_template)
  const slots = Array.from(templates.join(' ').matchAll(/\{\{(\w+)\}\}/g), (m) => m[1]).filter(
    (slot) => own.has(runnerKey(slot)),
  )
  const params = (schema.parameters ?? schema) as { properties?: object; required?: unknown[] }
  if (!slots.length || !params.properties) return schema
  const kept = {
    ...params,
    properties: omit(params.properties, slots),
    ...(Array.isArray(params.required) && {
      required: params.required.filter((name) => !slots.includes(name as string)),
    }),
  }
  return schema.parameters ? { ...schema, parameters: kept } : kept
}
